import type {
    SketchConstraintFormulaCompileContext,
    SketchConstraintFormulaCompileResult,
    SketchConstraintFormulaCompiler,
} from '../../../../types/sketch/solver/SketchConstraintFormulaCompiler';
import {SketchParametricEquationFactory} from './SketchParametricEquationFactory';

/**
 * 槽口关系公式编译器。
 *
 * 固定槽口由参数注册阶段把全部成员参数设为固定；相等槽口把结构尺度编译为等式，
 * 禁止逐点复制槽口几何或依赖拖拽后的局部夹持。
 */
export class SketchSlotConstraintFormulaCompiler implements SketchConstraintFormulaCompiler {
    /** 槽口关系类型。 */
    public readonly relationKinds = [
        'FixSlot',
        'EqualSlot',
    ] as const;

    /**
     * 编译槽口关系。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    public compile(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        switch (context.constraintState.relationKind) {
            case 'FixSlot':
                return this.compileFixSlot(context);
            case 'EqualSlot':
                return this.compileEqualSlot(context);
            default:
                return 'not-applicable';
        }
    }

    /**
     * 验证固定槽口已经声明结构成员。
     *
     * 实际固定参数由 island 参数注册阶段完成，此处不重复生成零残差方程。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    private compileFixSlot(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const fixedSlotIds = context.resolveDirectEntityIds()
            .filter((entityId) => context.slotMemberPointIds.has(entityId));

        if (fixedSlotIds.length === 0) {
            context.reportUnsupported('固定槽口关系的目标槽口缺少结构声明');
            return 'rejected';
        }

        const unfixedSlotIds = fixedSlotIds
            .filter((entityId) => !context.fixedSlotEntityIds.has(entityId));

        if (unfixedSlotIds.length > 0) {
            context.reportUnsupported(`固定槽口存在未冻结结构参数的目标：${unfixedSlotIds.join('、')}`);
            return 'rejected';
        }

        return 'compiled';
    }

    /**
     * 编译相等槽口关系。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    private compileEqualSlot(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const slotIds = context.resolveDirectEntityIds()
            .filter((entityId) => context.slotStructureParameterIds.has(entityId));

        if (slotIds.length < 2) {
            context.reportUnsupported('相等槽口关系需要两个以上已声明结构的槽口');
            return 'rejected';
        }

        const equationId = `constraint:${context.constraintState.constraintId}`;
        const base = context.slotStructureParameterIds.get(slotIds[0]!)!;

        slotIds.slice(1).forEach((slotId, index) => {
            const other = context.slotStructureParameterIds.get(slotId)!;

            context.system.addEquation(SketchParametricEquationFactory.scalarEqual(
                `${equationId}:half-width:${index}`,
                base.halfWidthParameterId,
                other.halfWidthParameterId,
            ));

            if (base.radiusParameterId && other.radiusParameterId) {
                context.system.addEquation(SketchParametricEquationFactory.scalarEqual(
                    `${equationId}:radius:${index}`,
                    base.radiusParameterId,
                    other.radiusParameterId,
                ));
            }

            if (base.axisHandles && other.axisHandles) {
                context.system.addEquation(SketchParametricEquationFactory.equalLength(
                    `${equationId}:axis-length:${index}`,
                    base.axisHandles[0],
                    base.axisHandles[1],
                    other.axisHandles[0],
                    other.axisHandles[1],
                ));
            }

            if (base.startAngleParameterId && base.endAngleParameterId
                && other.startAngleParameterId && other.endAngleParameterId) {
                context.system.addEquation(SketchParametricEquationFactory.scalarSpanEqual(
                    `${equationId}:sweep:${index}`,
                    base.startAngleParameterId,
                    base.endAngleParameterId,
                    other.startAngleParameterId,
                    other.endAngleParameterId,
                ));
            }
        });

        return 'compiled';
    }
}
